/**
 * THE BOOTSTRAP CONTRACT — the single source of truth for both sides.
 *
 * The SDK *writes* these params into the URL fragment; the console *reads* them
 * back out. They are two halves of one agreement, and if they ever disagree the
 * console silently boots with no API base and no bet — an integration bug that
 * looks like "betlens is broken".
 *
 * That is exactly why the SDK lives inside the frontend repo: both sides import
 * this file, so a rename is a typecheck error rather than a production surprise.
 *
 *   parent panel ──(fragment)──► console ──(fetch)──► panel backend
 *
 * Everything here travels in the **fragment**, never the query string: the
 * fragment is not sent to the server hosting the console, so the token stays out
 * of its logs.
 */

export const HASH_PARAMS = {
  /** Short-lived JWT minted by the host panel. */
  token: 't',
  /** Base URL of the panel's mounted betlens API. */
  api: 'api',
  /** 'dark' | 'light' — omitted entirely for 'auto'. */
  theme: 'theme',
  /** play_service_id (kept short — it is in every URL). */
  playServiceId: 'psid',
  gameUuid: 'game_uuid',
  betId: 'bet_id',
  transactionId: 'transaction_id',
  cycleUniqId: 'cycle_uniq_id',
  trUniqId: 'tr_uniq_id',
  /** PT5: a whole round (multi-stake games, e.g. blackjack). */
  roundId: 'round_id',
} as const;

export type HashParam = (typeof HASH_PARAMS)[keyof typeof HASH_PARAMS];

/**
 * Resolve the API base the console should call.
 *
 * A panel may pass `api` as an absolute URL, or as a **root-relative path**
 * (`/api/v1/betlens`). The relative form exists because a panel usually cannot
 * name its own public hostname: one built bundle is deployed to dev, staging and
 * prod, whose hostnames differ. The console, however, is in a position to
 * resolve it — a framed document can read the origin of the page that embedded
 * it, and that page IS the panel.
 *
 * ⚠️ Only a RELATIVE api is ever resolved this way. An absolute value is used
 * verbatim, so the embedder can never redirect a call that the panel pinned. And
 * a relative api with no embedder (the console opened directly in a tab) returns
 * null rather than silently resolving against the console's own origin, which
 * would fetch Cloudflare instead of a backend and look like an outage.
 */
export function resolveApiBase(
  api: string | null | undefined,
  embedderOrigin: string | null,
): string | null {
  const value = (api ?? '').trim();
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value.replace(/\/+$/, '');
  if (!value.startsWith('/')) return null; // see assertApiBase — must be root-relative
  if (!embedderOrigin) return null;
  try {
    return new URL(value, embedderOrigin).toString().replace(/\/+$/, '');
  } catch {
    return null;
  }
}

/**
 * Throws unless `api` is either absolute or root-relative.
 *
 * A path like `api/v1/betlens` (no leading slash) would resolve against the
 * embedder's current PATH, so the same panel would produce a different backend
 * URL depending on which page the console was opened from. Rejecting it in the
 * panel is far kinder than debugging that from inside an iframe.
 */
export function assertApiBase(api: string): void {
  if (!api) throw new BetlensError('api is required (the panel-mounted betlens base URL)');
  if (/^https?:\/\//i.test(api) || api.startsWith('/')) return;
  throw new BetlensError(
    `api must be an absolute URL or a root-relative path starting with "/" — got "${api}"`,
  );
}

/** Four ways to identify a bet — mirrors the backend's input contract. */
export interface BetlensLookup {
  play_service_id: string;
  /** PT1: with bet_id. */
  game_uuid?: string;
  /** PT1: with game_uuid. */
  bet_id?: string;
  /** PT2: our internal transactions.id (bet, win OR rollback — never client_trx_id). */
  transaction_id?: string;
  /** PT3: the bet-level hash — the master join key. */
  cycle_uniq_id?: string;
  /** PT4: the per-transaction hash (unique); resolves to its whole bet. */
  tr_uniq_id?: string;
  /** PT5: a whole round (multi-stake games); needs game_uuid. */
  round_id?: string;
}

export type BetlensTheme = 'dark' | 'light' | 'auto';

export class BetlensError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BetlensError';
  }
}

/**
 * Throws unless the lookup is a valid PT1 or PT2.
 *
 * Shared so the panel rejects a bad call where the developer is standing, and
 * the console rejects the same shape when it arrives via a hand-made URL.
 */
export function assertLookup(lookup: BetlensLookup): void {
  if (!lookup.play_service_id) {
    throw new BetlensError('lookup.play_service_id is required');
  }
  const pt1 = Boolean(lookup.game_uuid && lookup.bet_id);
  const pt2 = Boolean(lookup.transaction_id);
  const pt3 = Boolean(lookup.cycle_uniq_id);
  const pt4 = Boolean(lookup.tr_uniq_id);
  const pt5 = Boolean(lookup.game_uuid && lookup.round_id);
  if (!pt1 && !pt2 && !pt3 && !pt4 && !pt5) {
    throw new BetlensError(
      'lookup must be one of: { game_uuid, bet_id } (PT1), { transaction_id } (PT2), ' +
        '{ cycle_uniq_id } (PT3), { tr_uniq_id } (PT4), or { game_uuid, round_id } (PT5)',
    );
  }
}
